"use client";

import { useLocale, useTranslations } from "next-intl";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { features } from "@/data/main-data";

export default function Why() {
  const t = useTranslations("Home");
  const locale = useLocale();
  const isArabic = locale === "ar";

  return (
    <section className="bg-[#0A0C12] border-y border-[#C8A96E]/20 relative py-24 md:py-32 overflow-hidden">
      <div className="absolute inset-0 opacity-10 bg-[linear-gradient(rgba(200,169,110,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(200,169,110,0.1)_1px,transparent_1px)] bg-size-[40px_40px]" />
      <div className="absolute top-0 left-0 w-full h-full bg-linear-to-b from-[#0A0C12] via-transparent to-[#0A0C12] z-0" />

      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-150 h-75 bg-[#C8A96E]/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="md:container px-4 md:px-0 lg:w-[85%] mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="w-8 h-px bg-[#C8A96E]" />
            <span className="text-[#C8A96E] uppercase tracking-[0.4em] text-[10px] md:text-xs font-bold font-cairo">
              {t("whySubtitle")}
            </span>
            <div className="w-8 h-px bg-[#C8A96E]" />
          </div>

          <h2 className="text-3xl md:text-5xl lg:text-6xl font-black text-[#EDE5D6] mb-6 font-cairo">
            {t("partners")}
          </h2>

          <p className="mx-auto max-w-2xl leading-relaxed text-[#EDE5D6]/50 text-sm md:text-base font-light font-cairo">
            {t("partnerDes")}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title_en}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              viewport={{ once: true }}
              className="group relative"
            >
              <div className="relative z-10 border border-[#C8A96E]/10 bg-[#11141D]/80 backdrop-blur-sm p-8 h-full flex flex-col transition-all duration-500 hover:border-[#C8A96E]/40 hover:-translate-y-2 group-hover:shadow-[0_20px_50px_rgba(200,169,110,0.05)]">
                <div className="relative mb-8">
                  <div className="w-14 h-14 rounded-lg bg-linear-to-br from-[#C8A96E]/20 to-transparent flex items-center justify-center border border-[#C8A96E]/20 group-hover:from-[#C8A96E]/30 transition-all duration-300">
                    <feature.icon className="w-7 h-7 text-[#C8A96E]" />
                  </div>
                  <span className="absolute -top-4 -right-2 text-4xl font-black text-white/2 group-hover:text-[#C8A96E]/5] transition-colors duration-500">
                    0{index + 1}
                  </span>
                </div>

                <h3 className="text-xl font-bold text-[#EDE5D6] mb-4 group-hover:text-[#C8A96E] transition-colors duration-300 font-cairo">
                  {isArabic ? feature.title_ar : feature.title_en}
                </h3>

                <p className="text-[#EDE5D6]/40 leading-relaxed text-sm font-light mb-8 font-cairo">
                  {isArabic ? feature.description_ar : feature.description_en}
                </p>

                <div className="mt-auto flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-[#C8A96E] opacity-0 group-hover:opacity-100 transition-all duration-500 translate-y-2 group-hover:translate-y-0">
                  <span>{isArabic ? "اكتشف المزيد" : "Learn More"}</span>
                  <ArrowUpRight size={12} />
                </div>
              </div>

              <div className="absolute inset-0 bg-linear-to-br from-[#C8A96E]/20 to-transparent opacity-0 group-hover:opacity-100 blur-2xl transition-opacity duration-500 -z-10" />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
